import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Instagram, Mail, MessageSquare, MapPin, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';
import axios from 'axios';
import { CONTACT } from '../mock';
import SectionHead from './SectionHead';
import { Reveal } from './reveal';
import { track } from '../lib/track';
import { useToast } from '../hooks/use-toast';

// Rough email format check — good enough for a UX guard; the backend and
// EmailJS are the ultimate validators.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_MESSAGE_LEN = 4;

// Pull the buyer's most recent shipping estimate (if any) so we can
// attach it to the lead. Silently returns null on any failure — this
// data is enrichment, not a hard requirement.
function readLastEstimate() {
  try {
    const raw = window.localStorage.getItem('last_estimate');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

// Static line arrays used by the InfoBlock components. Extracted outside the
// component so their identity is stable and children don't re-render needlessly.
const INSTAGRAM_LINES = [CONTACT.instagram];
const EMAIL_LINES = [CONTACT.email];
const AREA_LINES = [CONTACT.area];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '', smsConsent: false });
  const [submitting, setSubmitting] = useState(false);
  const messageRef = useRef(null);
  // Tracks the last prefill event source so we can stamp the lead with
  // where it originated (e.g. abandoned-estimate-nudge, financing).
  const leadChannelRef = useRef(null);
  // Hidden calculator fields — populated by CostCalculator via a global
  // ref + `calculator-state` event. Submitted alongside the visible
  // form fields through both EmailJS and /api/leads.
  const [calc, setCalc] = useState(() => (typeof window !== 'undefined' && window.__calcState) || {
    calc_used: 'false',
    calc_materials_compared: '',
    calc_time_horizon_years: '',
    calc_total_hdpe: '',
    calc_total_steel: '',
    calc_total_aluminum: '',
    calc_breakeven_vs_steel: '',
    calc_breakeven_vs_aluminum: '',
    calc_revloss_hdpe: '',
    calc_revloss_steel: '',
    calc_revloss_aluminum: '',
  });

  useEffect(() => {
    function onCalc(e) { if (e && e.detail) setCalc(e.detail); }
    window.addEventListener('calculator-state', onCalc);
    return () => window.removeEventListener('calculator-state', onCalc);
  }, []);

  // Listen for cross-section "prefill this message into the quote form"
  // requests (e.g. the Financing → Request an Application button). We
  // fill the message field, scroll the form into view, and focus the
  // textarea so the user can edit / tap Send.
  useEffect(() => {
    function onPrefill(e) {
      const message = e?.detail?.message;
      if (typeof message !== 'string' || !message) return;
      if (e?.detail?.source) leadChannelRef.current = e.detail.source;
      setForm((prev) => ({ ...prev, message }));
      const el = document.getElementById('quote-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus after scroll settles.
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.focus();
          try {
            messageRef.current.setSelectionRange(message.length, message.length);
          } catch (_) {}
        }
      }, 700);
    }
    window.addEventListener('prefill-quote-message', onPrefill);
    return () => window.removeEventListener('prefill-quote-message', onPrefill);
  }, []);

  // CONTACT is a module-level import so its identity is stable; the memo
  // just prevents recreating the mapped array on every render.
  const phoneLines = useMemo(
    () => CONTACT.phones.map((p) => `${p.number} — ${p.name}`),
    []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );

  const submit = async (e) => {
    e.preventDefault();
    const trimmedMessage = form.message.trim();
    if (!form.name.trim()) {
      toast({ title: 'Missing name', description: 'Please tell us your name.' });
      return;
    }
    if (!form.phone.trim()) {
      toast({ title: 'Missing phone', description: 'Please add a phone number so we can respond.' });
      return;
    }
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) {
      toast({ title: 'Email required', description: 'Please add a valid email address.' });
      return;
    }
    if (trimmedMessage.length < MIN_MESSAGE_LEN) {
      toast({
        title: 'Message too short',
        description: `Please add a message with at least ${MIN_MESSAGE_LEN} characters so we can help.`,
      });
      return;
    }
    if (!form.smsConsent) {
      toast({
        title: 'One more step',
        description: 'Please check the box to consent to calls and SMS messages so we can respond to your inquiry.',
      });
      return;
    }
    setSubmitting(true);
    // Fire-and-forget: save the lead to our backend DB alongside the
    // EmailJS send. We don't await or block on failures here — the
    // customer-facing email flow must always fire, even if the DB is down.
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message || null,
        source: 'barge',
        sms_consent: form.smsConsent,
        last_estimate: readLastEstimate(),
        lead_channel: leadChannelRef.current,
        ...calc,
      })
      .catch(() => {});
    try {
      // EmailJS single send. The Contact Us template has an Auto-Reply
      // template linked in the EmailJS dashboard, so the customer
      // confirmation email fires automatically from the same call.
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: form.name,
          phone: form.phone,
          email: form.email,
          message: form.message || '(no additional details)',
          sms_consent: form.smsConsent ? 'YES — checked SMS consent on absolutebarges.com contact form' : 'NO',
          time: new Date().toLocaleString('en-US', {
            timeZone: 'America/New_York',
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
          ...calc,
        },
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      toast({ title: 'Message sent', description: "We'll reach out within 24 hours." });
      track('lead_submit', { source: 'barge' });
      setForm({ name: '', phone: '', email: '', message: '', smsConsent: false });
      // Lead is captured — clear the estimate so the abandoned-nudge and
      // sticky bar don't nag someone who's already in our funnel.
      try {
        window.localStorage.removeItem('last_estimate');
      } catch (_) {}
    } catch (err) {
      toast({
        title: "Couldn't send message",
        description:
          (err && (err.text || err.message))
            ? `${err.text || err.message}. If this persists, call ${CONTACT.salesPhone.number}.`
            : `Please try again or call ${CONTACT.salesPhone.number} directly.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-16 sm:py-20 lg:py-32 bg-[#050608] border-t border-white/5">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead eyebrow="Get In Touch" title="Contact Us"
          body="Tell us what your new barge needs to be capable of, share any concerns, or ask us any questions that you have and we'll get back to you ASAP." />

        <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10">
          <Reveal delay={0.15} className="space-y-7">
            <InfoBlock
              icon={<Phone className="w-5 h-5" />}
              label="Phone"
              lines={phoneLines}
            />
            <InfoBlock
              icon={<Mail className="w-5 h-5" />}
              label="Email"
              lines={EMAIL_LINES}
              link={`mailto:${CONTACT.email}`}
            />
            <InfoBlock
              icon={<Instagram className="w-5 h-5" />}
              label="Instagram"
              lines={INSTAGRAM_LINES}
              link={CONTACT.instagramUrl}
            />
            <InfoBlock
              icon={<MapPin className="w-5 h-5" />}
              label="Service Area"
              lines={AREA_LINES}
            />
            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href={`tel:${CONTACT.salesPhone.tel}`}
                onClick={() => track('contact_click', { method: 'call' })}
                className="btn-teal px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
              >
                <Phone className="w-4 h-4" /> Call {CONTACT.salesPhone.number}
              </a>
              <a
                href={`sms:${CONTACT.salesPhone.tel}`}
                onClick={() => track('contact_click', { method: 'sms' })}
                className="btn-ghost px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" /> Text us
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
          <form id="quote-form" onSubmit={submit} className="panel rounded-xl p-6 sm:p-7 lg:p-9 scroll-mt-24">
            <h3 className="font-display text-white text-lg sm:text-xl font-semibold tracking-wide">Request a Quote</h3>
            <p className="text-zinc-500 text-sm mt-1">We respond within one business day.</p>

            <div className="mt-7 space-y-5">
              {/* Hidden calculator fields — submitted through EmailJS and
                  /api/leads via the params object above. Rendered here so
                  they also travel with any native form-post fallback and
                  so the CRM's custom field names appear on the DOM for
                  debugging. Keys match server config exactly. */}
              {Object.entries(calc).map(([k, v]) => (
                <input key={k} type="hidden" name={k} value={v ?? ''} readOnly data-testid={`hidden-${k}`} />
              ))}
              <Field label={<>Your Name <span className="text-cyan-300">*</span></>}>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="John Doe"
                />
              </Field>
              <Field label={<>Phone <span className="text-cyan-300">*</span></>}>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="(321) 000-0000"
                />
              </Field>
              <Field label={<>Email <span className="text-cyan-300">*</span></>}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label={<>Message <span className="text-cyan-300">*</span></>}>
                <textarea
                  ref={messageRef}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  data-lenis-prevent
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white resize-none overscroll-contain"
                  placeholder="Tell us what you want on your new barge or any questions you have."
                />
              </Field>

              {/* Honeypot — hidden field for spam protection */}
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                style={{ display: 'none' }}
              />

              {/* A2P / TCPA-compliant SMS consent — validated in the JS
                  submit handler (not via native `required`) so that the
                  branded toast UI shows instead of a browser bubble. */}
              <label className="flex gap-3 items-start cursor-pointer group" data-testid="sms-consent-block">
                <input
                  type="checkbox"
                  checked={form.smsConsent}
                  onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })}
                  className="mt-1 w-4 h-4 shrink-0 accent-cyan-400 cursor-pointer"
                  data-testid="sms-consent-checkbox"
                />
                <span className="text-[12px] leading-relaxed text-zinc-400 group-hover:text-zinc-300 transition-colors">
                  <span className="text-cyan-300">*</span> By checking this box and submitting this form, I consent to receive calls and SMS/MMS text messages from{' '}
                  <span className="text-zinc-200">Absolute Marine Equipment</span> at the phone number provided, related to my inquiry, quote, order, and delivery. Message frequency varies. Message &amp; data rates may apply. Reply{' '}
                  <span className="text-zinc-200">STOP</span> to opt out or{' '}
                  <span className="text-zinc-200">HELP</span> for help. Consent is not a condition of purchase. See our{' '}
                  <Link to="/privacy" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">Privacy Policy</Link>{' '}
                  and{' '}
                  <Link to="/sms-terms" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">SMS Terms</Link>.
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="btn-teal w-full px-5 py-3 rounded-md inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.18em] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Send Message'}
              </button>
            </div>
          </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function InfoBlock({ icon, label, lines, link }) {
  const content = (
    <div className="flex gap-4 items-start">
      <div className="w-10 h-10 rounded-md bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500 mb-1.5">{label}</div>
        {lines.map((l) => (
          <div key={l} className="text-zinc-200 font-medium">{l}</div>
        ))}
      </div>
    </div>
  );
  if (!link) return content;
  const external = /^https?:/i.test(link);
  return (
    <a
      href={link}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      className="block hover:opacity-90"
    >
      {content}
    </a>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">{label}</div>
      {children}
    </label>
  );
}
